import type {
  WinnerImportAlreadyImported, WinnerImportCustomerDecision, WinnerImportPreview, WinnerImportProjectDecision, WinnerImportResult,
} from './winner-import.api'

export type WinnerImportStage = 'drop' | 'reading' | 'review' | 'creating' | 'done' | 'stopped'

export type ReviewBlock = 'customer' | 'project' | 'categories' | 'supply' | 'quote'

/**
 * The blocks the user confirms. From inside a project the customer and the job site are already
 * known: only the quote is left to look at. The Winner groups are asked only when one of them is
 * new, renamed or filed under a hidden category (S08), and the catalogues only when one of them is
 * not answered yet (S12): a second import asks nothing.
 */
export function reviewBlocks(fromProject: boolean, askCategories = false, askSupply = false): ReviewBlock[] {
  const blocks: ReviewBlock[] = fromProject ? [] : ['customer', 'project']
  if (askCategories) blocks.push('categories')
  if (askSupply) blocks.push('supply')
  blocks.push('quote')
  return blocks
}

/**
 * How far along the job file is, 0-100. Reading the file already earns a third: Soly did the typing.
 * Each confirmed block moves the bar; creation finishes it. Monotonic within a run by construction.
 */
export function importProgress(stage: WinnerImportStage, confirmed: number, total: number): number {
  switch (stage) {
    case 'drop':
      return 0
    case 'stopped':
      return 0
    case 'reading':
      return 20
    case 'review':
      return Math.round(35 + (55 * Math.min(confirmed, total)) / Math.max(total, 1))
    case 'creating':
      return 95
    case 'done':
      return 100
  }
}

/** The sentences Soly says while it reads, built from what it actually found. */
export function narration(preview: WinnerImportPreview): string[] {
  const steps: string[] = []
  const file = preview.file
  if (file) {
    const dealer = file.dealerName ? ` de ${file.dealerName}` : ''
    const version = file.winnerVersion ? ` (Winner ${file.winnerVersion})` : ''
    steps.push(`${file.documentKind}${dealer} reconnu${version}`)
  }
  if (preview.customer) {
    const number = preview.customer.winnerNumber ? ` · n° Winner ${preview.customer.winnerNumber}` : ''
    steps.push(`Client : ${preview.customer.displayName}${number}`)
  }
  if (preview.project) steps.push(`Chantier : ${preview.project.suggestedName}`)
  if (preview.quote) {
    const suppliers = preview.quote.suppliers.length
    steps.push(`${preview.quote.articleCount} articles chez ${suppliers} fournisseur${suppliers > 1 ? 's' : ''}`)
    steps.push(
      preview.quote.isBalanced
        ? `Totaux vérifiés au cent près : ${formatEuro(preview.quote.winnerTotals.totalIncVat)} TTC`
        : `Écart de totaux détecté : ${formatEuro(preview.quote.winnerTotals.totalIncVat - preview.quote.solyTotals.totalIncVat)}`
    )
  }
  return steps
}

/**
 * What the import made, and only that. Winner's supplier codes are counted, not promised as
 * orders: nothing is ordered until the sale exists and someone prepares the purchases.
 */
export function doneSummary(result: Pick<WinnerImportResult, 'articleCount' | 'supplierCount'>): string {
  const lines = `${result.articleCount} ligne${result.articleCount > 1 ? 's' : ''}`
  const suppliers = `${result.supplierCount} fournisseur${result.supplierCount > 1 ? 's' : ''} Winner repéré${result.supplierCount > 1 ? 's' : ''}`
  return `Devis créé : ${lines}, ${suppliers}`
}

/**
 * An existing project belongs to one customer. When the customer changes, a project picked for the
 * previous one is dropped back to « new project », so the import cannot land in another customer's
 * project unseen. A new-project decision (name, type, address) carries over.
 */
export function projectAfterCustomerChange(
  previous: WinnerImportCustomerDecision,
  next: WinnerImportCustomerDecision,
  project: WinnerImportProjectDecision,
): WinnerImportProjectDecision {
  const sameCustomer = previous.mode === next.mode && previous.customerId === next.customerId
  if (sameCustomer || project.mode !== 'existing') return project
  return { ...project, mode: 'new', projectId: undefined }
}

const NO_ACCESS = '00000000-0000-0000-0000-000000000000'

const importedAtFormat = new Intl.DateTimeFormat('fr-BE', {
  timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

/** « Ce fichier a déjà été importé le 21/10/2026 à 14:32 dans « Cuisine » ». The project only when the user may open it. */
export function alreadyImportedMessage(seen: WinnerImportAlreadyImported): string {
  let message = 'Ce fichier a déjà été importé'
  if (seen.importedAt) {
    const parts = Object.fromEntries(importedAtFormat.formatToParts(new Date(seen.importedAt)).map((p) => [p.type, p.value]))
    message += ` le ${parts.day}/${parts.month}/${parts.year} à ${parts.hour}:${parts.minute}`
  }
  if (seen.projectName) message += ` dans « ${seen.projectName} »`
  return `${message}.`
}

/**
 * Where « Voir le devis » goes: the quote's editor while it is a draft; once issued, the project's
 * quotes, where its PDF and source file are; nowhere when the project is someone else's.
 */
export function alreadyImportedTarget(seen: WinnerImportAlreadyImported): string | null {
  if (!seen.projectId || seen.projectId === NO_ACCESS) return null
  if (seen.versionId && seen.versionStatus === 'draft')
    return `/projets/${seen.projectId}/devis-v2/${seen.quoteId}/versions/${seen.versionId}/edit`
  return `/projects/${seen.projectId}?tab=quotes`
}

/** Minutes of typing the import saved: half a minute per article line, the office's own estimate. */
export function minutesSaved(articleCount: number): number {
  return Math.max(1, Math.round(articleCount * 0.5))
}

export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(amount)
}

export function formatDuration(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1000))
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`
}
